import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Loader2,
  Search,
  Calendar,
  Phone,
  Trash2,
  AlertCircle,
  Save,
  Eye,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  getContacts,
  getLeadMasters,
  updateContact,
  deleteContact,
} from '../../services/contactService';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const emptyMasters = {
  leadStatuses: [],
  leadStages: [],
  salesStages: [],
};

const ContactList = () => {
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission('contacts.update');
  const canDelete = hasPermission('contacts.delete');
  const [contacts, setContacts] = useState([]);
  const [masters, setMasters] = useState(emptyMasters);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const buildDrafts = (items) => {
    return items.reduce((acc, item) => {
      acc[item.id] = {
        lead_status_id: item.lead_status_id || '',
        lead_stage_id: item.lead_stage_id || '',
        sales_stage_id: item.sales_stage_id || '',
        lost_reason: item.lost_reason || '',
        note: item.note || '',
      };
      return acc;
    }, {});
  };

  const fetchLeadMasters = useCallback(async () => {
    try {
      const response = await getLeadMasters();
      if (response.success) {
        setMasters(response.data || emptyMasters);
      }
    } catch (error) {
      toast.error('Failed to fetch lead dropdown options');
    }
  }, []);

  const fetchContacts = useCallback(async (searchQuery = '') => {
    try {
      setLoading(true);
      const response = await getContacts(searchQuery);
      if (response.success) {
        setContacts(response.data);
        setDrafts(buildDrafts(response.data));
        return response.data;
      } else {
        toast.error(response.message || 'Failed to fetch leads');
      }
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchContacts(debouncedSearchTerm);
  }, [debouncedSearchTerm, fetchContacts]);

  useEffect(() => {
    fetchLeadMasters();
  }, [fetchLeadMasters]);

  const updateDraft = (id, field, value) => {
    setDrafts((current) => ({
      ...current,
      [id]: {
        ...current[id],
        [field]: value,
      },
    }));
  };

  const handleSave = async (id) => {
    try {
      setSavingId(id);
      const response = await updateContact(id, drafts[id]);
      if (response.success) {
        toast.success('Lead updated successfully');
        const updatedContacts = await fetchContacts(debouncedSearchTerm);
        const refreshedLead = updatedContacts?.find((item) => item.id === id);
        if (refreshedLead) {
          setSelectedLead(refreshedLead);
        }
      } else {
        toast.error(response.message || 'Failed to update lead');
      }
    } catch (error) {
      toast.error('Failed to update lead');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;

    try {
      setIsDeleting(true);
      const response = await deleteContact(contactToDelete);
      if (response.success) {
        toast.success('Lead deleted successfully');
        setDeleteDialogOpen(false);
        setContactToDelete(null);
        fetchContacts(debouncedSearchTerm);
      } else {
        toast.error(response.message || 'Failed to delete lead');
      }
    } catch (error) {
      toast.error('Failed to delete lead');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    const normalizedDate =
      typeof dateString === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateString)
        ? `${dateString.replace(' ', 'T')}Z`
        : dateString;

    return new Date(normalizedDate).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };

  const renderMasterSelect = (id, field, items, label) => (
    <select
      value={drafts[id]?.[field] || ''}
      onChange={(event) => updateDraft(id, field, event.target.value)}
      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-primary"
      aria-label={label}
      disabled={!canUpdate}
    >
      <option value="">Select {label}</option>
      {items.map((item) => (
        <option key={item.id} value={item.id}>
          {item.name}
        </option>
      ))}
    </select>
  );

  const getMasterName = (items, id) => {
    return items.find((item) => String(item.id) === String(id))?.name || '-';
  };

  const selectedDraft = selectedLead ? drafts[selectedLead.id] || {} : {};

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-primary/10 text-primary rounded-2xl">
              <Mail size={28} />
            </span>
            Lead Enquiries
          </h1>
          <p className="text-slate-500 mt-1 font-medium">
            Review website enquiries and manage lead progress.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by lead ID, name, email, phone or service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-2xl border-slate-200 focus-visible:ring-primary"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 rounded-3xl border border-dashed border-slate-300">
          <Loader2 className="animate-spin text-primary mb-4" size={40} />
          <p className="text-slate-500 font-medium">Loading leads...</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-100 shadow-sm">
          <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-400">
            <Mail size={40} />
          </div>
          <h3 className="text-xl font-bold text-slate-800">No leads yet</h3>
          <p className="text-slate-500 mb-8 max-w-xs mx-auto">
            Website enquiries will appear here once visitors submit the form.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="min-w-[140px]">Lead ID</TableHead>
                <TableHead className="min-w-[220px]">Customer</TableHead>
                <TableHead className="min-w-[220px]">Service</TableHead>
                <TableHead className="min-w-[160px]">Source</TableHead>
                <TableHead className="min-w-[150px]">Lead Status</TableHead>
                <TableHead className="min-w-[160px]">Date</TableHead>
                <TableHead className="text-right min-w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                  onClick={() => setSelectedLead(item)}
                >
                  <TableCell>
                    <Badge variant="outline" className="w-fit bg-slate-50 text-slate-700 border-slate-200">
                      {item.lead_id || `LEAD-${String(item.id).padStart(3, '0')}`}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-bold text-slate-900">{item.name}</span>
                      <a
                        href={`mailto:${item.email}`}
                        onClick={(event) => event.stopPropagation()}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Mail size={12} className="text-slate-400" /> {item.email}
                      </a>
                      <a
                        href={`https://wa.me/${String(item.whatsapp || '').replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="text-xs text-slate-600 hover:text-green-600 flex items-center gap-1"
                      >
                        <Phone size={12} className="text-slate-400" /> {item.whatsapp}
                      </a>
                    </div>
                  </TableCell>

                  <TableCell>
                    <span className="text-sm text-slate-700">
                      {item.service || <span className="text-slate-300 italic">Not selected</span>}
                    </span>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs">
                      <span className="font-semibold text-slate-800">{item.utm_source || 'Direct'}</span>
                      <span className="text-slate-400">{item.utm_medium || '-'}</span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-100">
                      {item.lead_status_name || getMasterName(masters.leadStatuses, drafts[item.id]?.lead_status_id)}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col gap-1 text-[12px] font-medium text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} /> {formatDate(item.created_at)}
                      </span>
                      <span className="pl-5 text-[11px] text-slate-400">
                        Updated: {formatDate(item.updated_at)}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canDelete && <Button
                        variant="ghost"
                        size="icon"
                        className="text-slate-500 hover:text-primary hover:bg-primary/10 rounded-xl"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedLead(item);
                        }}
                        title="View Lead"
                      >
                        <Eye size={18} />
                      </Button>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl"
                        onClick={(event) => {
                          event.stopPropagation();
                          setContactToDelete(item.id);
                          setDeleteDialogOpen(true);
                        }}
                        title="Delete Lead"
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={Boolean(selectedLead)} onOpenChange={(open) => !open && setSelectedLead(null)}>
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-900">
                  {selectedLead.lead_id || `LEAD-${String(selectedLead.id).padStart(3, '0')}`}
                </DialogTitle>
                <DialogDescription>
                  Complete lead details and internal management fields.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-4">
                  <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">Contact</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="block text-slate-400">Name</span>
                      <span className="font-semibold text-slate-900">{selectedLead.name}</span>
                    </div>
                    <div>
                      <span className="block text-slate-400">Email</span>
                      <a href={`mailto:${selectedLead.email}`} className="font-semibold text-primary hover:underline">
                        {selectedLead.email}
                      </a>
                    </div>
                    <div>
                      <span className="block text-slate-400">Phone</span>
                      <a
                        href={`https://wa.me/${String(selectedLead.whatsapp || '').replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-slate-900 hover:text-green-600"
                      >
                        {selectedLead.whatsapp}
                      </a>
                    </div>
                    <div>
                      <span className="block text-slate-400">Captured On</span>
                      <span className="font-semibold text-slate-900">{formatDate(selectedLead.created_at)}</span>
                    </div>
                    <div>
                      <span className="block text-slate-400">Updated On</span>
                      <span className="font-semibold text-slate-900">{formatDate(selectedLead.updated_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">Requirement</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="block text-slate-400">Service</span>
                      <span className="font-semibold text-slate-900">{selectedLead.service || '-'}</span>
                    </div>
                    <div>
                      <span className="block text-slate-400">Budget</span>
                      <span className="font-semibold text-slate-900">{selectedLead.budget || '-'}</span>
                    </div>
                    <div>
                      <span className="block text-slate-400">Project Start Date</span>
                      <span className="font-semibold text-slate-900">{selectedLead.start_date || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">Attribution</h3>
                <div className="grid gap-4 text-sm md:grid-cols-3">
                  <div>
                    <span className="block text-slate-400">Source</span>
                    <span className="font-semibold text-slate-900">{selectedLead.utm_source || 'Direct'}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">Medium</span>
                    <span className="font-semibold text-slate-900">{selectedLead.utm_medium || '-'}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">Campaign</span>
                    <span className="font-semibold text-slate-900">{selectedLead.utm_campaign || '-'}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">Term</span>
                    <span className="font-semibold text-slate-900">{selectedLead.utm_term || '-'}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">Content</span>
                    <span className="font-semibold text-slate-900">{selectedLead.utm_content || '-'}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400">Click ID</span>
                    <span className="font-semibold text-slate-900">{selectedLead.gclid || selectedLead.fbclid || '-'}</span>
                  </div>
                  <div className="md:col-span-3">
                    <span className="block text-slate-400">Landing Page</span>
                    <span className="break-all font-semibold text-slate-900">{selectedLead.landing_page || '-'}</span>
                  </div>
                  <div className="md:col-span-3">
                    <span className="block text-slate-400">Referrer</span>
                    <span className="break-all font-semibold text-slate-900">{selectedLead.referrer || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">Project Details</h3>
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {selectedLead.idea || 'No project description provided.'}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="mb-4 text-sm font-bold uppercase tracking-widest text-slate-400">Lead Management</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  {renderMasterSelect(selectedLead.id, 'lead_status_id', masters.leadStatuses, 'lead status')}
                  {renderMasterSelect(selectedLead.id, 'lead_stage_id', masters.leadStages, 'lead stage')}
                  {renderMasterSelect(selectedLead.id, 'sales_stage_id', masters.salesStages, 'sales stage')}
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <input
                    value={selectedDraft.lost_reason || ''}
                    onChange={(event) => updateDraft(selectedLead.id, 'lost_reason', event.target.value)}
                    placeholder="Lost reason"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
                    disabled={!canUpdate}
                  />
                  <textarea
                    value={selectedDraft.note || ''}
                    onChange={(event) => updateDraft(selectedLead.id, 'note', event.target.value)}
                    placeholder="Internal note"
                    rows={3}
                    className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary md:col-span-2"
                    disabled={!canUpdate}
                  />
                </div>
              </div>

              <DialogFooter className="gap-3">
                {canUpdate && <Button
                  variant="outline"
                  onClick={() => setSelectedLead(null)}
                  className="rounded-2xl border-slate-200"
                >
                  Close
                </Button>}
                <Button
                  onClick={() => handleSave(selectedLead.id)}
                  disabled={savingId === selectedLead.id}
                  className="rounded-2xl bg-primary hover:bg-primary/90 text-secondary gap-2"
                >
                  {savingId === selectedLead.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                  Save Lead
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="text-red-500" size={24} />
            </div>
            <DialogTitle className="text-center text-xl font-bold text-slate-900">Confirm Deletion</DialogTitle>
            <DialogDescription className="text-center text-slate-500 mt-2">
              Are you sure you want to delete this lead? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1 rounded-2xl border-slate-200"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 rounded-2xl bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactList;
